import {
	type ColumnDef,
	flexRender,
	type RowData,
	useTable,
} from "@tanstack/react-table";
import { map, size } from "lodash";
import { useTranslation } from "react-i18next";
import { type DataTableFeatures, dataTableFeatures } from "./features";
import {
	StyledTable,
	StyledTableBody,
	StyledTableBorder,
	StyledTableCell,
	StyledTableContainer,
	StyledTableHead,
	StyledTableHeader,
	StyledTableRow,
} from "./styles";

interface DataTableProps<TData extends RowData> {
	columns: ColumnDef<DataTableFeatures, TData>[];
	data: TData[];
}

function DataTable<TData extends RowData>({
	columns,
	data,
}: DataTableProps<TData>) {
	const { t } = useTranslation();
	const table = useTable<DataTableFeatures, TData>({
		features: dataTableFeatures,
		data,
		columns,
	});

	return (
		<StyledTableContainer>
			<StyledTableBorder>
				<StyledTable>
					<StyledTableHeader>
						{map(table.getHeaderGroups(), (headerGroup) => (
							<StyledTableRow key={headerGroup.id}>
								{map(headerGroup.headers, (header) => (
									<StyledTableHead key={header.id}>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</StyledTableHead>
								))}
							</StyledTableRow>
						))}
					</StyledTableHeader>
					<StyledTableBody>
						{size(table.getRowModel().rows) ? (
							map(table.getRowModel().rows, (row) => (
								<StyledTableRow key={row.id}>
									{map(row.getAllCells(), (cell) => (
										<StyledTableCell key={cell.id}>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</StyledTableCell>
									))}
								</StyledTableRow>
							))
						) : (
							<StyledTableRow>
								<StyledTableCell
									colSpan={columns.length}
									className="h-24 text-center"
								>
									{t("components.dataTable.noResults")}
								</StyledTableCell>
							</StyledTableRow>
						)}
					</StyledTableBody>
				</StyledTable>
			</StyledTableBorder>
		</StyledTableContainer>
	);
}

export default DataTable;
